import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { type ColumnDef } from "@tanstack/react-table";
import { listPagesAdmin, deletePage } from "@/lib/api/pages.functions";
import { DataTable } from "@/components/admin/DataTable";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { AdminPageShell } from "@/components/admin/AdminPageShell";
import { StatusBadge } from "@/components/admin/StatusBadge";
import { tableActionsColumn, TableRowActions } from "@/components/admin/TableRowActions";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/pages/")({
  loader: () => listPagesAdmin({ data: {} }),
  component: AdminPagesList,
});

type Row = Awaited<ReturnType<typeof listPagesAdmin>>[number];

function pagePath(slug: string) {
  if (slug === "home") return "/";
  if (slug === "about") return "/about";
  return `/p/${slug}`;
}

function buildColumns(onDelete: (row: Row) => Promise<void>): ColumnDef<Row>[] {
  return [
  {
    accessorKey: "title",
    header: "Title",
    cell: ({ row }) => <span className="font-medium">{row.original.title}</span>,
  },
  {
    accessorKey: "slug",
    header: "Slug",
    cell: ({ row }) => (
      <span className="text-muted-foreground">
        {pagePath(row.original.slug)}
        {row.original.isSystemPage ? " · system" : ""}
      </span>
    ),
    meta: { filterVariant: "text" },
  },
  {
    accessorKey: "isSystemPage",
    header: "System",
    meta: { filterVariant: "select", groupable: true },
    cell: ({ row }) => (row.original.isSystemPage ? "Yes" : "No"),
  },
  {
    accessorKey: "status",
    header: "Status",
    meta: { filterVariant: "select", groupable: true },
    cell: ({ row }) => <StatusBadge status={row.original.status} />,
  },
  {
    ...tableActionsColumn<Row>(),
    cell: ({ row }) => (
      <TableRowActions
        edit={{ to: "/admin/pages/$id", params: { id: row.original.id } }}
        onDelete={row.original.isSystemPage ? undefined : () => onDelete(row.original)}
        deleteLabel={row.original.title}
        showDelete={!row.original.isSystemPage}
      />
    ),
  },
];
}

function AdminPagesList() {
  const data = Route.useLoaderData();
  const router = useRouter();

  async function handleDelete(row: Row) {
    try {
      await deletePage({ data: { id: row.id } });
      toast.success("Page deleted");
      router.invalidate();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to delete");
      throw e;
    }
  }

  const columns = buildColumns(handleDelete);

  return (
    <AdminPageShell>
      <AdminPageHeader
        title="Pages"
        action={
          <Button asChild>
            <Link to="/admin/pages/new">
              <Plus className="mr-2 h-4 w-4" /> New page
            </Link>
          </Button>
        }
      />
      <DataTable tableId="pages" columns={columns} data={data} />
    </AdminPageShell>
  );
}
